import { Reveal, MaskedLine } from "@/components/Reveal";
import { IMG } from "@/data/images";
import { useSEO } from "@/components/Seo";
import { eyebrow } from "@/lib/styles";

const CHAPTERS = [
  {
    title: "Struggle",
    text: "I had just turned 34 when I realized I was moving through life without putting anything forward. There was a stretch where I could not hold down a permanent job, and I could not help my family carry the responsibilities that were mine. So late at night, in my room, I started making things. That is where Threads Unbound™ began.",
    img: IMG.smoke,
  },
  {
    title: "Growth",
    text: "My family showed me something simple. We all fall short sometimes, and we can still build ourselves up for more. That is what growth means to me. Not a glow up. Just showing up again the next night, thread after thread.",
    img: "/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/66b5ef06-9f18-4a16-a768-5063e71fefd7.jpg",
  },
  {
    title: "Connection",
    text: "Threads mean connection. To everything people have a relationship with, not just clothes. The first time a customer told me they really liked a piece, I was shocked. And proud. Someone out there connected with something I made alone at night in my room.",
    img: "/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/9a263025-1563-45f4-b53d-2a330777908b.jpg",
  },
  {
    title: "Freedom",
    text: "Freedom is the pillar closest to me. Not escape. Arrival. The moment the thing that held you down becomes the thing that holds your art together. That is the unbinding.",
    img: "/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/a1bc76e6-fe17-4609-b464-9d371713e7b8.jpg",
  },
  {
    title: "Striving",
    text: "If you ask me what this brand is really about, it is one word. Striving. I want anyone who wears this work or hangs it on their wall to feel like they can do anything they put their mind to. It may not start great. It can still become something more.",
    img: "/api/files/threads-unbound/uploads/6a93ec78cc88050207c3cc9e/6884366a-96e6-458b-9084-1cc5cd800223.jpg",
  },
];

export default function Story() {
  useSEO(
    "Our Story | Threads Unbound™",
    "Threads Unbound™ started late at night in one room in Hialeah. This is the story behind the work."
  );

  return (
    <main data-testid="story-page">
      <header className="px-6 pb-24 pt-36 md:px-12 md:pt-44 lg:px-20">
        <MaskedLine>
          <span className={eyebrow}>Our story</span>
        </MaskedLine>
        <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.15}>
            <span className="block max-w-4xl text-5xl font-light sm:text-6xl lg:text-7xl">
              Every thread has a <span className="italic text-[#D4AF37]">breaking point</span>.
            </span>
          </MaskedLine>
          <MaskedLine delay={0.3}>
            <span className="block max-w-4xl text-5xl font-light sm:text-6xl lg:text-7xl">Mine became a beginning.</span>
          </MaskedLine>
        </h1>
      </header>

      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32 lg:px-20" data-testid="story-philosophy">
        <Reveal>
          <p className="max-w-3xl font-serif text-2xl font-light leading-relaxed text-white/80 md:text-3xl">
            Threads Unbound™ exists for everyone who has ever felt behind in their own life. The work is not
            decoration. It is proof. Proof that starting late still counts, and that falling short is not the same as
            being finished.
          </p>
        </Reveal>
      </section>

      {CHAPTERS.map((c, i) => (
        <section
          key={c.title}
          data-testid={`story-chapter-${c.title.toLowerCase()}`}
          className="relative overflow-hidden border-t border-white/10 px-6 py-20 md:px-12 md:py-28 lg:px-20"
        >
          <div className="grid items-center gap-14 md:grid-cols-2">
            <Reveal className={i % 2 === 1 ? "md:order-2" : ""}>
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/80">
                {String(i + 1).padStart(2, "0")} / {String(CHAPTERS.length).padStart(2, "0")}
              </p>
              <h2 className="mt-4 font-serif text-4xl font-light tracking-tight md:text-5xl">{c.title}</h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">{c.text}</p>
            </Reveal>
            <Reveal delay={0.15} className={i % 2 === 1 ? "md:order-1" : ""}>
              <div className="group aspect-[4/3] overflow-hidden border border-white/10">
                <img
                  src={c.img}
                  alt={c.title}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </div>
            </Reveal>
          </div>
        </section>
      ))}

      <section className="border-t border-white/10 px-6 py-32 text-center md:py-44" data-testid="story-pullquote">
        <Reveal>
          <blockquote className="mx-auto max-w-4xl font-serif text-3xl font-light italic leading-snug text-white/85 md:text-5xl">
            "Nobody hands you the pattern. You start with one loose thread and a reason to keep pulling."
          </blockquote>
          <p className="mt-8 text-xs uppercase tracking-[0.35em] text-[#D4AF37]">David, founder</p>
          <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-white/30">
            Made late at night in Atlanta, Georgia
          </p>
        </Reveal>
      </section>
    </main>
  );
}
